/**
 * From //www.domain.com.au/2012831395
 * To http://www.domain.com.au/3102-30-wellington-street-bondi-nsw-2026-2012831395
 */

// remove multiple continuous - by single -
const formatSeo = (unFormattedSeo: string): string =>
  unFormattedSeo.replace(/[^a-zA-Z0-9-]/g, '-').replace(/-+/g, '-');

const escapeRegex = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const normalizeTextAddress = (address: string): string =>
  address
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const normalizeSlugAddress = (address: string): string =>
  formatSeo(address.toLowerCase());

const VALID_STATES = ['nsw', 'vic', 'nt', 'wa', 'sa', 'tas', 'qld', 'act'];

const isValidState = (state: string): boolean =>
  VALID_STATES.includes(state.toLowerCase());

const isValidPostcode = (postcode: string): boolean => /^\d{4}$/.test(postcode);

const getAddressLocationPartsPresence = ({
  address,
  state,
  postcode,
}: {
  address: string;
  state: string;
  postcode: string;
}): { hasState: boolean; hasPostcode: boolean } => {
  const textAddress = normalizeTextAddress(address);
  const slugAddress = normalizeSlugAddress(address);

  const stateInText = new RegExp(`\\b${escapeRegex(state)}\\b`);
  const stateInSlug = new RegExp(`(^|-)${escapeRegex(state)}($|-)`);
  const postcodeInText = new RegExp(`\\b${escapeRegex(postcode)}\\b`);
  const postcodeInSlug = new RegExp(`(^|-)${escapeRegex(postcode)}($|-)`);

  return {
    hasState: stateInText.test(textAddress) || stateInSlug.test(slugAddress),
    hasPostcode:
      postcodeInText.test(textAddress) || postcodeInSlug.test(slugAddress),
  };
};

const removeDuplicateStatePostcodePairs = ({
  slug,
  state,
  postcode,
}: {
  slug: string;
  state: string;
  postcode: string;
}): string => {
  const repeatedPair = new RegExp(
    `(${escapeRegex(state)}-${escapeRegex(postcode)})(-\\1)+`,
    'g',
  );

  return slug.replace(repeatedPair, '$1');
};

export type Params = {
  displayableAddress?: string;
  state: string;
  postcode: string;
  adId: number;
};

export default ({
  displayableAddress = '',
  state,
  postcode,
  adId,
}: Params): string => {
  const parts: Array<string | number> = [];

  const normalizedState = state?.trim().toLowerCase();
  const normalizedPostcode = String(postcode || '').trim();
  const canUseStateAndPostcode =
    Boolean(normalizedState) &&
    Boolean(normalizedPostcode) &&
    isValidState(normalizedState) &&
    isValidPostcode(normalizedPostcode);

  if (displayableAddress) {
    parts.push(displayableAddress);

    const locationPresence = canUseStateAndPostcode
      ? getAddressLocationPartsPresence({
          address: displayableAddress,
          state: normalizedState,
          postcode: normalizedPostcode,
        })
      : { hasState: false, hasPostcode: false };

    if (canUseStateAndPostcode) {
      if (!locationPresence.hasState) {
        parts.push(normalizedState);
      }
      if (!locationPresence.hasPostcode) {
        parts.push(normalizedPostcode);
      }
    }
  } else if (canUseStateAndPostcode) {
    parts.push(normalizedState, normalizedPostcode);
  }
  parts.push(adId);

  const unFormattedSeo = parts.join('-').toLowerCase();
  const formattedSeo = formatSeo(unFormattedSeo);
  const dedupedSeo =
    normalizedState && normalizedPostcode
      ? removeDuplicateStatePostcodePairs({
          slug: formattedSeo,
          state: normalizedState,
          postcode: normalizedPostcode,
        })
      : formattedSeo;

  return `//www.domain.com.au/${dedupedSeo}`;
};
